import { useState, useEffect, useCallback, forwardRef, useRef, ChangeEvent } from 'react';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import debounce from 'lodash/debounce';
import { FaSearch, FaMagic } from 'react-icons/fa';
import { BiImage, BiCamera } from 'react-icons/bi';

import { Modal, ModalMethods, Text, Loader, toasts } from '../../../../ui-component';
import { api } from '../../../../utils/apiRequest';
import { getValueFromLocalStorage } from '../../../../utils/common';

export type IconType = {
  id?: string;
  icon_id?: string;
  url?: string;
  icon_url?: string;
  image_url?: string;
  short_lived_url?: string;
  name?: string;
  item_name?: string;
  reference_id?: string;
};

type IconSelectorModalProps = {
  onIconSelect: (icon: IconType) => void;
  selectedIconId?: string;
  fileType?: string;
};

const IconSelectorModal = forwardRef<ModalMethods, IconSelectorModalProps>(
  ({ onIconSelect, selectedIconId, fileType = '5' }, ref) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [icons, setIcons] = useState<IconType[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const fetchIcons = useCallback(async () => {
      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id) {
        toasts('error', 'Boutique ID not found', 'boutique-id-missing');

        return;
      }

      setIsLoading(true);

      try {
        const response = await api.getRequest('outfit_piece/icons');

        if (response.status && !_isNil(response.data)) {
          // API returns array of { reference_id, image_url }
          const iconsData: IconType[] = Array.isArray(response.data)
            ? response.data.map((item: any) => ({
                id: item.reference_id,
                icon_id: item.reference_id,
                reference_id: item.reference_id,
                url: item.image_url,
                icon_url: item.image_url,
                image_url: item.image_url,
                short_lived_url: item.image_url,
                name: item.reference_id,
              }))
            : [];

          setIcons(iconsData);
        } else {
          toasts('error', response.message || 'Failed to fetch icons', 'fetch-icons-error');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'fetch-icons-error');
        }
      } finally {
        setIsLoading(false);
      }
    }, []);

    const searchIcons = useCallback(
      async (query: string) => {
        const boutique_id = getValueFromLocalStorage('boutique_id');

        if (!boutique_id) {
          toasts('error', 'Boutique ID not found', 'boutique-id-missing');

          return;
        }

        if (_isEmpty(query.trim())) {
          fetchIcons();

          return;
        }

        setIsLoading(true);

        try {
          const encodedQuery = encodeURIComponent(query.trim());
          const response = await api.getRequest(`outfit_piece/icons/search?name=${encodedQuery}`);

          if (response.status && !_isNil(response.data)) {
            // API returns array of { reference_id, image_url }
            const searchResults: IconType[] = Array.isArray(response.data)
              ? response.data.map((item: any) => ({
                  id: item.reference_id,
                  icon_id: item.reference_id,
                  reference_id: item.reference_id,
                  url: item.image_url,
                  icon_url: item.image_url,
                  short_lived_url: item.image_url,
                  name: query.trim(),
                }))
              : [];

            setIcons(searchResults);
          } else {
            toasts('error', response.message || 'Failed to search icons', 'search-icons-error');
            setIcons([]);
          }
        } catch (err) {
          if (err instanceof Error) {
            toasts('error', err.message, 'search-icons-error');
          }
          setIcons([]);
        } finally {
          setIsLoading(false);
        }
      },
      [fetchIcons]
    );

    const debouncedSearchIconsRef = useRef(
      debounce((query: string) => {
        void searchIcons(query);
      }, 500)
    );

    // Update debounced function when searchIcons changes
    useEffect(() => {
      debouncedSearchIconsRef.current.cancel();
      debouncedSearchIconsRef.current = debounce((query: string) => {
        void searchIcons(query);
      }, 500);
    }, [searchIcons]);

    const prevSearchQueryRef = useRef<string>('');
    const isInitialMountRef = useRef(true);

    // Handle search query changes and initial fetch
    useEffect(() => {
      const trimmedQuery = searchQuery.trim();
      const prevTrimmedQuery = prevSearchQueryRef.current.trim();

      if (_isEmpty(trimmedQuery)) {
        // Query is empty - fetch all icons
        if (!_isEmpty(prevTrimmedQuery)) {
          // Switching from search to empty - cancel pending searches
          debouncedSearchIconsRef.current.cancel();
        }
        // Fetch icons on initial mount or when clearing search

        if (isInitialMountRef.current || !_isEmpty(prevTrimmedQuery)) {
          void fetchIcons();
        }
      } else {
        // Query has text - use debounced search
        debouncedSearchIconsRef.current(trimmedQuery);
      }

      prevSearchQueryRef.current = searchQuery;
      isInitialMountRef.current = false;
    }, [searchQuery, fetchIcons]);

    // Cleanup: cancel pending debounce only on unmount
    useEffect(() => {
      return () => {
        debouncedSearchIconsRef.current.cancel();
      };
    }, []);

    const handleIconClick = (icon: IconType) => {
      onIconSelect(icon);

      if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
        ref.current.hide();
      }
    };

    const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
      if (!_isNil(e.target.files) && e.target.files[0]) {
        setIsUploading(true);

        try {
          const formDataUpload = new FormData();
          const file = e.target.files[0];

          formDataUpload.append('file', file);
          formDataUpload.append('file_type', fileType);

          const response = await api.postRequest(
            'storage/upload_multiple_files',
            formDataUpload,
            true
          );

          if (response.status && !_isNil(response.data) && response.data.response?.length > 0) {
            const uploadedImage = response.data.response[0];

            // Convert uploaded image to IconType format
            const iconData: IconType = {
              id: uploadedImage.reference_id,
              icon_id: uploadedImage.reference_id,
              reference_id: uploadedImage.reference_id,
              url: uploadedImage.short_lived_url,
              icon_url: uploadedImage.short_lived_url,
              short_lived_url: uploadedImage.short_lived_url,
              name: file.name,
            };

            onIconSelect(iconData);

            if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
              ref.current.hide();
            }
          } else {
            toasts('error', 'Failed to upload icon', 'icon-upload-error');
          }
        } catch (err) {
          if (err instanceof Error) {
            toasts('error', err.message, 'icon-upload-error');
          }
        } finally {
          setIsUploading(false);
          // Reset file input

          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      }
    };

    const handleAddFromGallery = () => {
      fileInputRef.current?.click();
    };

    const handleCamera = () => {
      // TODO: Implement camera functionality
      toasts('info', 'Camera feature coming soon', 'camera-feature');
    };

    const handleCreateWithAI = () => {
      // TODO: Implement AI creation functionality
      toasts('info', 'AI creation coming soon', 'ai-creation');
    };

    return (
      <Modal
        ref={ref}
        size="xl"
        title="Select, Upload or create an icon"
        showFooter={false}
        showCloseIcon={true}
      >
        <div className="flex flex-col h-full min-h-[400px]">
          <div className="mb-4">
            <Text className="font-inter font-semibold text-sm mb-2 block" color="black">
              Select an icon
            </Text>
            <div className="relative flex items-center w-full">
              <FaSearch className="absolute left-3 text-gray-500 pointer-events-none" size={16} />
              <input
                type="text"
                placeholder="Search by item name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-10 pl-10 pr-3 border border-gray-300 rounded-lg text-sm font-inter outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500"
              />
            </div>
          </div>

          {isLoading || isUploading ? (
            <div className="flex justify-center items-center h-64">
              <Loader />
            </div>
          ) : (
            <>
              {icons.length > 0 ? (
                <div
                  className="flex flex-wrap gap-3 py-4 px-2 max-h-[400px] overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:hover:bg-gray-500"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#888 #f1f1f1',
                  }}
                >
                  {icons.map((icon, index) => {
                    // Use the same logic as in handleIconSelect for consistency
                    const iconId = icon.reference_id || icon.id || icon.icon_id || `icon-${index}`;
                    const iconUrl = icon?.image_url || icon.icon_url || icon.url || '';
                    const iconName = icon.name || icon.item_name || '';
                    const isSelected = selectedIconId === iconId;

                    return (
                      <div
                        key={iconId}
                        onClick={() => handleIconClick(icon)}
                        className={`relative w-20 h-20 min-w-[80px] aspect-square rounded-lg bg-white cursor-pointer flex items-center justify-center overflow-hidden transition-all hover:border-blue-500 hover:scale-105 hover:shadow-md ${
                          isSelected
                            ? 'border-2 border-blue-500'
                            : 'border-2 border-gray-300 hover:border-blue-500'
                        }`}
                      >
                        <img
                          src={iconUrl}
                          alt={iconName}
                          className="w-full h-full object-contain p-2"
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center justify-center h-[200px] p-5">
                  <Text color="gray" size="small">
                    {searchQuery ? 'No icons found matching your search' : 'No icons available'}
                  </Text>
                </div>
              )}
            </>
          )}

          <div className="flex gap-4 py-4 border-t border-gray-300 mt-auto">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png, image/jpeg, image/jpg"
              className="hidden"
              onChange={handleFileUpload}
              disabled={isUploading}
            />
            <button
              type="button"
              onClick={handleAddFromGallery}
              disabled={isUploading}
              className="flex items-center gap-2 px-4 py-2.5 bg-transparent border border-gray-300 rounded-lg cursor-pointer transition-all hover:bg-gray-50 hover:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <BiImage size={20} className="text-black" />
              <Text size="small" color="black">
                {isUploading ? 'Uploading...' : 'Add from gallery'}
              </Text>
            </button>
            <button
              type="button"
              onClick={handleCamera}
              className="flex items-center gap-2 px-4 py-2.5 bg-transparent border border-gray-300 rounded-lg cursor-pointer transition-all hover:bg-gray-50 hover:border-blue-500"
            >
              <BiCamera size={20} className="text-black" />
              <Text size="small" color="black">
                Camera
              </Text>
            </button>
            <button
              type="button"
              onClick={handleCreateWithAI}
              className="flex items-center gap-2 px-4 py-2.5 bg-transparent border border-gray-300 rounded-lg cursor-pointer transition-all hover:bg-gray-50 hover:border-blue-500"
            >
              <FaMagic size={20} className="text-black" />
              <Text size="small" color="black">
                Create with AI
              </Text>
            </button>
          </div>
        </div>
      </Modal>
    );
  }
);

IconSelectorModal.displayName = 'IconSelectorModal';

export default IconSelectorModal;
